import {
  type Control,
  useControl,
  useControlEffect,
} from "@react-typed-forms/core";
import { AppForm } from "hwc-common/hwc/AppForm.tsx";
import { isFluentError, useApiClient } from "@astroapps/client";
import { defaultApplicationForm } from "hwc-common/hwc/schemas.ts";
import { ApiException, ApplicationClient } from "hwc-common/hwc/client.ts";
import { applyValidationErrors } from "hwc-common/hwc/validation.ts";

type ApplicationFormProps = {
  code: Control<string | undefined>;
};

export function ApplicationForm({ code }: ApplicationFormProps) {
  const client = useApiClient(ApplicationClient);
  const data = useControl(() => defaultApplicationForm);
  const success = useControl(false);
  const error = useControl<string | null>(null);

  // Clear the error banner once the user edits the form.
  useControlEffect(
    () => data.value,
    () => {
      error.value = null;
    },
  );

  async function submitApplication() {
    error.value = null;
    try {
      // Validate form?
      const valid = data.validate();
      data.touched = true;
      if (valid) {
        // Submit application
        await client.submitApplication({
          code: code.value!,
          form: data.value,
        });

        success.value = true;
      }
    } catch (e: unknown) {
      console.error(e);
      // The generated client throws the parsed body directly for typed 400s,
      // and wraps in ApiException otherwise — unwrap to get the response body.
      const body =
        ApiException.isApiException(e) && e.status === 400 && e.response
          ? JSON.parse(e.response)
          : e;
      if (isFluentError(body)) {
        // Field-level validation errors → map onto the form controls.
        applyValidationErrors(data)(body);
      } else {
        // Anything else → surface the ProblemDetails message.
        error.value =
          body.detail ??
          "Something went wrong submitting your application. Please try again.";
      }
    }
  }

  return (
    <div className="mt-10 flex justify-center">
      <div className="plate w-full px-6 pt-8 pb-8 sm:px-10">
        <div className="-mt-12 mb-6 flex justify-center">
          <span className="plate-tab">
            {success.value ? "Submitted" : "New member details"}
          </span>
        </div>

        {success.value ? (
          <div className="py-6 text-center">
            <i className="fas fa-check-circle text-3xl text-primary-600" />
            <h2 className="mt-3 text-2xl font-semibold text-secondary-800 uppercase">
              Application received
            </h2>
            <p className="mt-2 text-surface-600">
              We've sent a confirmation link to{" "}
              <span className="font-semibold text-secondary-800">
                {data.value.email}
              </span>
              . Open it to confirm your membership application.
            </p>
            <div className="mt-6">
              <button
                className="btn btn-primary"
                onClick={() => (code.value = undefined)}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            {error.value && (
              <div className="mb-5 rounded border border-danger-200 bg-danger-50 px-4 py-3 text-center text-danger-700">
                <i className="fas fa-circle-xmark mr-1" /> {error.value}
              </div>
            )}
            <AppForm formType="Application" data={data} />
            <div className="arch-rule mt-8 mb-6" />
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => submitApplication()}
                className="btn btn-primary"
              >
                Submit application
              </button>
              <button
                onClick={() => (code.value = undefined)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
